import type { UseFormReturn } from 'react-hook-form';

import { FormCheckbox, FormDateInput, FormField, FormInput, FormNumberInput } from '@/_components';
import { FormCurrencyInput } from '@/_containers';
import { currencyInput } from '@/_schemas/generic';
import { dateTime } from '@/_shared';

import { useTerminationLogic } from './use-termination-logic';

import type { PartnerFormSchema } from '../schemas';

type TerminationFormFieldsProps = {
  form: UseFormReturn<PartnerFormSchema>;
  isEditable?: boolean;
};

export function TerminationFormFields({ form, isEditable = true }: TerminationFormFieldsProps) {
  useTerminationLogic(form);
  const [tailEnabled, transitionEnabled] = form.watch(['tail_enabled', 'transition_tail_enabled']);

  return (
    <>
      <FormField
        control={form.control}
        name="termination_notice_date"
        render={({ field: { value, onChange } }) => (
          <FormDateInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Notice of Termination Date"
            value={value}
            onValueChange={onChange}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="required_notice_days"
        render={({ field: { value, onChange } }) => (
          <FormNumberInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Required Notice Period (Days)"
            value={value ?? ''}
            onValueChange={(values) => {
              const numValue = values.floatValue;
              onChange(numValue !== undefined ? numValue : null);
            }}
            disabled={!isEditable}
            allowNegative={false}
            decimalScale={0}
          />
        )}
      />

      <FormField
        control={form.control}
        name="calculated_termination_date"
        render={({ field: { value, onChange } }) => (
          <FormInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Calculated Termination Date"
            value={value ? dateTime.format(value, 'MM/DD/YYYY') : ''}
            disabled
            placeholder="Auto-calculated"
            className="bg-muted cursor-not-allowed"
            onChange={onChange}
          />
        )}
      />

      <FormField
        control={form.control}
        name="override_termination_date"
        render={({ field: { value, onChange } }) => (
          <FormDateInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Override Termination Date (Optional)"
            value={value}
            onValueChange={onChange}
            disabled={!isEditable}
          />
        )}
      />

      <FormField
        control={form.control}
        name="termination_extension_days"
        render={({ field: { value, onChange } }) => (
          <FormNumberInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Extension to Termination (Days)"
            value={value ?? ''}
            onValueChange={(values) => {
              const numValue = values.floatValue;
              onChange(numValue !== undefined ? numValue : null);
            }}
            disabled={!isEditable}
            allowNegative={false}
            decimalScale={0}
          />
        )}
      />

      <FormField
        control={form.control}
        name="final_termination_date"
        render={({ field: { value, onChange } }) => (
          <FormInput
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
            label="Final Termination Date"
            value={value ? dateTime.format(value, 'MM/DD/YYYY') : ''}
            disabled
            placeholder="Auto-calculated"
            className="bg-muted cursor-not-allowed font-semibold"
            onChange={onChange}
          />
        )}
      />

      <FormField
        control={form.control}
        name="tail_enabled"
        render={({ field }) => (
          <div className="col-span-12">
            <FormCheckbox
              checked={!!field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
                // Clear tail fields when disabled
                if (!e.target.checked) {
                  form.setValue('tail_percentage', null);
                  form.setValue('tail_duration_months', null);
                  form.setValue('tail_flat_monthly_payout', currencyInput.defaultValue());
                  form.setValue('tail_start_month', null);
                }
              }}
              disabled={!isEditable}
              label="Tail Compensation"
            />
          </div>
        )}
      />

      {tailEnabled && (
        <>
          <FormField
            control={form.control}
            name="tail_percentage"
            render={({ field: { value, onChange } }) => (
              <FormNumberInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Tail Percentage (%)"
                value={value ?? ''}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  onChange(numValue !== undefined ? numValue : null);
                }}
                disabled={!isEditable}
                suffix="%"
                allowNegative={false}
                decimalScale={2}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="tail_duration_months"
            render={({ field: { value, onChange } }) => (
              <FormNumberInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Tail Duration (Months)"
                value={value ?? ''}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  onChange(numValue !== undefined ? numValue : null);
                }}
                disabled={!isEditable}
                allowNegative={false}
                decimalScale={0}
              />
            )}
          />

          <FormCurrencyInput
            name="tail_flat_monthly_payout"
            label="Flat Additional Payout per Month"
            isEditable={isEditable}
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
          />

          <FormField
            control={form.control}
            name="tail_start_month"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Start Month for Tail"
                value={value}
                onValueChange={onChange}
                disabled={!isEditable}
              />
            )}
          />
        </>
      )}

      <FormField
        control={form.control}
        name="transition_tail_enabled"
        render={({ field }) => (
          <div className="col-span-12">
            <FormCheckbox
              checked={!!field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
                // Clear transition fields when disabled
                if (!e.target.checked) {
                  form.setValue('transition_tail_percentage', null);
                  form.setValue('transition_tail_duration_months', null);
                  form.setValue('transition_tail_start_month', null);
                }
              }}
              disabled={!isEditable}
              label="Transition Period Compensation"
            />
          </div>
        )}
      />

      {transitionEnabled && (
        <>
          <FormField
            control={form.control}
            name="transition_tail_percentage"
            render={({ field: { value, onChange } }) => (
              <FormNumberInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Transition Tail Percentage (%)"
                value={value ?? ''}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  onChange(numValue !== undefined ? numValue : null);
                }}
                disabled={!isEditable}
                suffix="%"
                allowNegative={false}
                decimalScale={2}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="transition_tail_duration_months"
            render={({ field: { value, onChange } }) => (
              <FormNumberInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Transition Tail Duration (Months)"
                value={value ?? ''}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  onChange(numValue !== undefined ? numValue : null);
                }}
                disabled={!isEditable}
                allowNegative={false}
                decimalScale={0}
              />
            )}
          />

          <FormField
            control={form.control}
            name="transition_tail_start_month"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Start Month of Transition Tail"
                value={value}
                onValueChange={onChange}
                disabled={!isEditable}
              />
            )}
          />
        </>
      )}
    </>
  );
}
